import React from 'react';
import styled from 'styled-components';
import { CircularProgress, Tooltip } from '@material-ui/core';
import { ChevronRight } from '@material-ui/icons';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { formatDate } from '../../utils/status';
import { getOpenRatePoints } from '../../utils/graph';
import { BLUE, BORDER, GREY_TEXT } from '../../AnalyticsV2.styled';

const CHART_WIDTH = 240;
const CHART_HEIGHT = 120;
const AXIS_TICK = { fontSize: 10, fill: GREY_TEXT };

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 10px 24px 10px 16px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  cursor: default;
`;

// Same size as the chart, so the card does not change size while loading.
const ChartPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CHART_WIDTH}px;
  height: ${CHART_HEIGHT}px;
  font-size: 13px;
  color: ${GREY_TEXT};
  text-align: center;
`;

const Label = styled.div`
  font-size: 13px;
  color: #1c1c1c;
`;

const Heading = styled.div`
  font-size: 24px;
  font-weight: 500;
  color: #1c1c1c;
  white-space: nowrap;
`;

const ViewStats = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  color: #1c1c1c;
  cursor: pointer;
  &:hover {
    color: ${BLUE};
  }
  &:disabled {
    color: ${GREY_TEXT};
    cursor: default;
  }
`;

// Open rate per message for `messages`. "View stats" opens the combined
// stats page for the selected messages; it is disabled while nothing is
// selected. isLoading is true while the messages for the graph are being
// fetched.
const EngagementCard = ({
  messages,
  selectedCount,
  isLoading,
  onViewStats,
}) => {
  const points = getOpenRatePoints(messages);

  const renderChart = () => {
    if (isLoading) {
      return (
        <ChartPlaceholder>
          <CircularProgress size={28} style={{ color: BLUE }} />
        </ChartPlaceholder>
      );
    }
    // Messages with no delivered emails are not plotted.
    if (points.length === 0) {
      return <ChartPlaceholder>No delivered emails to show</ChartPlaceholder>;
    }
    return (
      <AreaChart
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        data={points}
        margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
      >
        <defs>
          <linearGradient id="analyticsV2OpenRate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
            <stop offset="100%" stopColor={BLUE} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
        {/* One tick per message, in send order (evenly spaced, not by date). */}
        <XAxis
          dataKey="sendTime"
          tickFormatter={sendTime => formatDate(sendTime).slice(0, 5)}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: BORDER }}
          interval="preserveStartEnd"
          minTickGap={12}
        />
        <YAxis
          domain={[0, 100]}
          ticks={[0, 50, 100]}
          tickFormatter={rate => `${rate}%`}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={false}
          width={34}
        />
        <Area
          type="monotone"
          dataKey="openRate"
          stroke={BLUE}
          strokeWidth={2.5}
          fill="url(#analyticsV2OpenRate)"
          dot={{ r: 3, fill: BLUE, stroke: BLUE }}
          isAnimationActive={false}
        />
      </AreaChart>
    );
  };

  return (
    <Tooltip
      title={
        selectedCount > 0
          ? 'Average Open Rate - Click for more Graphs'
          : 'Select at least one message to view stats'
      }
      placement="top"
    >
      <Card>
        {renderChart()}
        <div>
          <Label>
            {selectedCount > 0 ? `${selectedCount} selected` : 'All messages'}
          </Label>
          <Heading>Engagement overview</Heading>
          <ViewStats disabled={selectedCount === 0} onClick={onViewStats}>
            View stats <ChevronRight fontSize="small" />
          </ViewStats>
        </div>
      </Card>
    </Tooltip>
  );
};

export default EngagementCard;
